import { useState, useRef, useEffect } from 'react';
import type { ITag } from '@map-bldr/shared';
import { Checkbox } from './ui/checkbox';
import { Filter } from 'lucide-react';

export interface IFilterButtonProps {
  availableTags: ITag[];
  selectedTagFilters: string[];
  onTagFilterChange: (tagIds: string[]) => void;
}

export function FilterButton({
  availableTags,
  selectedTagFilters,
  onTagFilterChange,
}: IFilterButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggleTag = (tagId: string) => {
    if (selectedTagFilters.includes(tagId)) {
      onTagFilterChange(selectedTagFilters.filter(id => id !== tagId));
    } else {
      onTagFilterChange([...selectedTagFilters, tagId]);
    }
  };

  const handleToggleAll = () => {
    onTagFilterChange([]);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-accent/50 transition-colors text-xs font-mono text-muted-foreground"
      >
        <Filter className="w-3.5 h-3.5" />
        {selectedTagFilters.length > 0 && (
          <span className="text-primary font-semibold">{selectedTagFilters.length}</span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-md shadow-lg z-50 min-w-48 max-w-64">
          <div className="p-2 border-b border-border">
            <span className="text-xs font-mono text-muted-foreground">Filter by tags</span>
          </div>
          <div className="p-2 max-h-64 overflow-y-auto">
            <label className="flex items-center cursor-pointer text-xs font-mono px-2 py-1.5 hover:bg-accent/50 rounded transition-colors">
              <Checkbox
                checked={selectedTagFilters.length === 0}
                onCheckedChange={handleToggleAll}
                className="mr-2"
              />
              <span className="text-foreground">All</span>
            </label>
            {availableTags.map(tag => {
              const isSelected = selectedTagFilters.includes(tag.id);
              return (
                <label
                  key={tag.id}
                  className="flex items-center cursor-pointer text-xs font-mono px-2 py-1.5 hover:bg-accent/50 rounded transition-colors"
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => handleToggleTag(tag.id)}
                    className="mr-2"
                  />
                  <span className="text-foreground">#{tag.name}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
